'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { authService } from '@/lib/authService';
import { useAuth } from '@/hooks/useAuth';
import { useNavigation } from '@/hooks/useNavigation';

const FAVOURITES_CHANGED_EVENT = 'ta-favourites-changed';
const FAVOURITES_BAR_TOGGLE_EVENT = 'ta-favourites-bar-toggle';

/** Legacy global key (pre–per-client); read once for migration when bucket empty */
const LEGACY_FAVOURITES_STORAGE_KEY = 'ta_user_favourites';
const LEGACY_BAR_VISIBLE_KEY = 'ta_favourites_bar_visible';

export interface FavouriteItem {
  id: string;
  label: string;
  href: string;
  icon?: string;
  moduleKey?: string;
  section?: string;
  addedAt: string;
}

function favouritesStorageKey(clientId: number): string {
  return `ta_user_favourites_${clientId}`;
}

function barVisibleStorageKey(clientId: number): string {
  return `ta_favourites_bar_visible_${clientId}`;
}

/** Match Header / navigation: nav client_info first, then JWT client_id */
function resolveFavouritesClientId(
  clientInfo: { id?: unknown } | null | undefined,
  userClientId: unknown,
): number {
  const resolved = Number(clientInfo?.id ?? userClientId ?? 0);
  if (!Number.isFinite(resolved) || resolved <= 0) {
    return 0;
  }
  return resolved;
}

function readStoredFavourites(clientId: number): FavouriteItem[] {
  if (typeof window === 'undefined' || clientId <= 0) return [];
  try {
    const key = favouritesStorageKey(clientId);
    let raw = localStorage.getItem(key);
    // One-time: old global list belonged to primary tenant for most installs
    if (!raw && clientId === 1) {
      const legacy = localStorage.getItem(LEGACY_FAVOURITES_STORAGE_KEY);
      if (legacy) {
        localStorage.setItem(key, legacy);
        localStorage.removeItem(LEGACY_FAVOURITES_STORAGE_KEY);
        raw = legacy;
      }
    }
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function writeStoredFavourites(items: FavouriteItem[], clientId: number) {
  if (typeof window === 'undefined' || clientId <= 0) return;
  localStorage.setItem(favouritesStorageKey(clientId), JSON.stringify(items));
  queueMicrotask(() => {
    window.dispatchEvent(
      new CustomEvent(FAVOURITES_CHANGED_EVENT, { detail: { items, clientId } }),
    );
  });
}

function readBarVisible(clientId: number): boolean {
  if (typeof window === 'undefined' || clientId <= 0) return false;
  const key = barVisibleStorageKey(clientId);
  let v = localStorage.getItem(key);
  if (v == null && clientId === 1) {
    const legacy = localStorage.getItem(LEGACY_BAR_VISIBLE_KEY);
    if (legacy != null) {
      localStorage.setItem(key, legacy);
      localStorage.removeItem(LEGACY_BAR_VISIBLE_KEY);
      v = legacy;
    }
  }
  return v === 'true';
}

function writeBarVisible(visible: boolean, clientId: number) {
  if (typeof window === 'undefined' || clientId <= 0) return;
  localStorage.setItem(barVisibleStorageKey(clientId), String(visible));
  queueMicrotask(() => {
    window.dispatchEvent(
      new CustomEvent(FAVOURITES_BAR_TOGGLE_EVENT, { detail: { visible, clientId } }),
    );
  });
}

function generateFavouriteId(href: string, section?: string): string {
  const normalized = href.replace(/\/+$/, '').toLowerCase();
  const base = section ? `${normalized}#${section}` : normalized;
  let hash = 0;
  for (let i = 0; i < base.length; i++) {
    hash = ((hash << 5) - hash + base.charCodeAt(i)) | 0;
  }
  return `fav_${Math.abs(hash).toString(36)}`;
}

function preferenceApiKey(clientId: number): string {
  return `user_favourites_c${clientId}`;
}

export function useFavourites() {
  const { user } = useAuth();
  const { navigationData } = useNavigation();

  const clientId = useMemo(
    () => resolveFavouritesClientId(navigationData?.client_info, (user as { client_id?: number } | null)?.client_id),
    [navigationData?.client_info, user],
  );

  const clientIdRef = useRef(clientId);

  useEffect(() => {
    clientIdRef.current = clientId;
  }, [clientId]);

  const [favourites, setFavourites] = useState<FavouriteItem[]>([]);
  const [barVisible, setBarVisible] = useState(false);
  const syncTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // When the active client changes, load that client's bar state from localStorage
  useEffect(() => {
    queueMicrotask(() => {
      if (clientId <= 0) {
        setFavourites([]);
        setBarVisible(false);
        return;
      }
      setFavourites(readStoredFavourites(clientId));
      setBarVisible(readBarVisible(clientId));
    });
  }, [clientId]);

  useEffect(() => {
    const handleFavouritesChanged = (event: Event) => {
      const d = (event as CustomEvent<{ items: FavouriteItem[]; clientId: number }>).detail;
      if (typeof d?.clientId !== 'number' || d.clientId !== clientIdRef.current) return;
      if (Array.isArray(d.items)) setFavourites(d.items);
    };

    const handleBarToggle = (event: Event) => {
      const d = (event as CustomEvent<{ visible: boolean; clientId: number }>).detail;
      if (typeof d?.clientId !== 'number' || d.clientId !== clientIdRef.current) return;
      if (typeof d.visible === 'boolean') setBarVisible(d.visible);
    };

    const handleStorage = (e: StorageEvent) => {
      const cid = clientIdRef.current;
      if (cid <= 0) return;
      if (e.key === favouritesStorageKey(cid) && e.newValue != null) {
        try {
          setFavourites(JSON.parse(e.newValue) || []);
        } catch {
          setFavourites([]);
        }
      }
      if (e.key === barVisibleStorageKey(cid)) {
        setBarVisible(e.newValue === 'true');
      }
    };

    window.addEventListener(FAVOURITES_CHANGED_EVENT, handleFavouritesChanged as EventListener);
    window.addEventListener(FAVOURITES_BAR_TOGGLE_EVENT, handleBarToggle as EventListener);
    window.addEventListener('storage', handleStorage);

    return () => {
      window.removeEventListener(FAVOURITES_CHANGED_EVENT, handleFavouritesChanged as EventListener);
      window.removeEventListener(FAVOURITES_BAR_TOGGLE_EVENT, handleBarToggle as EventListener);
      window.removeEventListener('storage', handleStorage);
    };
  }, []);

  const schedulePersistToApi = useCallback((items: FavouriteItem[], cid: number) => {
    if (cid <= 0) return;
    if (syncTimeoutRef.current) clearTimeout(syncTimeoutRef.current);
    syncTimeoutRef.current = setTimeout(async () => {
      const token = authService.getTokens()?.access_token;
      if (!token) return;
      try {
        await fetch('/api/v1/preferences', {
          method: 'PATCH',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            key: preferenceApiKey(cid),
            value: items,
          }),
        });
      } catch {
        // Silently fail — localStorage is the primary store
      }
    }, 2000);
  }, []);

  const addFavourite = useCallback(
    (item: Omit<FavouriteItem, 'id' | 'addedAt'>) => {
      const cid = clientIdRef.current;
      if (cid <= 0) return;

      const id = generateFavouriteId(item.href, item.section);
      const newItem: FavouriteItem = {
        ...item,
        id,
        addedAt: new Date().toISOString(),
      };

      setFavourites((prev) => {
        if (prev.some((f) => f.id === id)) return prev;
        const next = [...prev, newItem];
        writeStoredFavourites(next, cid);
        schedulePersistToApi(next, cid);
        return next;
      });
    },
    [schedulePersistToApi],
  );

  const removeFavourite = useCallback(
    (id: string) => {
      const cid = clientIdRef.current;
      if (cid <= 0) return;

      setFavourites((prev) => {
        const next = prev.filter((f) => f.id !== id);
        if (next.length === prev.length) return prev;
        writeStoredFavourites(next, cid);
        schedulePersistToApi(next, cid);
        return next;
      });
    },
    [schedulePersistToApi],
  );

  const removeFavouriteByHref = useCallback(
    (href: string, section?: string) => {
      const id = generateFavouriteId(href, section);
      removeFavourite(id);
    },
    [removeFavourite],
  );

  const isFavourite = useCallback((href: string, section?: string): boolean => {
    const id = generateFavouriteId(href, section);
    return favourites.some((f) => f.id === id);
  }, [favourites]);

  const toggleFavourite = useCallback(
    (item: Omit<FavouriteItem, 'id' | 'addedAt'>) => {
      if (isFavourite(item.href, item.section)) {
        removeFavouriteByHref(item.href, item.section);
        return false;
      }
      addFavourite(item);
      return true;
    },
    [addFavourite, isFavourite, removeFavouriteByHref],
  );

  const reorderFavourites = useCallback(
    (fromIndex: number, toIndex: number) => {
      const cid = clientIdRef.current;
      if (cid <= 0) return;

      setFavourites((prev) => {
        const next = [...prev];
        const [moved] = next.splice(fromIndex, 1);
        next.splice(toIndex, 0, moved);
        writeStoredFavourites(next, cid);
        schedulePersistToApi(next, cid);
        return next;
      });
    },
    [schedulePersistToApi],
  );

  const toggleBarVisible = useCallback(() => {
    const cid = clientIdRef.current;
    if (cid <= 0) return;

    setBarVisible((prev) => {
      const next = !prev;
      writeBarVisible(next, cid);
      return next;
    });
  }, []);

  const setBarVisibleExplicit = useCallback((visible: boolean) => {
    const cid = clientIdRef.current;
    if (cid <= 0) return;

    setBarVisible(visible);
    writeBarVisible(visible, cid);
  }, []);

  const count = favourites.length;

  return {
    favourites,
    count,
    barVisible,
    clientId,
    addFavourite,
    removeFavourite,
    removeFavouriteByHref,
    isFavourite,
    toggleFavourite,
    reorderFavourites,
    toggleBarVisible,
    setBarVisible: setBarVisibleExplicit,
  };
}

export { FAVOURITES_BAR_TOGGLE_EVENT, FAVOURITES_CHANGED_EVENT };

export default useFavourites;
